import { bool, fail, isRecord, list, num, optionalText, sameResource, text } from '../../../shared/api/onecEnvelope'
import type { CashReceipt, CashReceiptDetails, CashReceiptKind, CashReceiptLine, CashReceipts, CashRegisters, CashSummary, CashUnitKind, CashBounds } from '../model/types'

/**
 * Декодеры чеков 1С (Resource.OneC.Contracts/CashReceipts). Каждая ссылка обязана быть про ЭТУ базу: чек чужой базы с
 * тем же GUID — ошибка ответа (D-064). GUID — в нижнем регистре.
 */
const KINDS: readonly CashReceiptKind[] = ['sale', 'refund']

function record(raw: unknown, field: string): Record<string, unknown> {
  return isRecord(raw) ? raw : fail(`${field} — не объект`)
}

function refKey(raw: unknown, field: string, resourceId: string): string {
  const ref = record(raw, field)
  const owner = isRecord(ref.resourceId) ? text(ref.resourceId.value, `${field}.resourceId.value`) : text(ref.resourceId, `${field}.resourceId`)
  if (!sameResource(owner, resourceId)) fail(`${field} ссылается на другую базу`)
  return text(ref.remoteKey, `${field}.remoteKey`).toLowerCase()
}

const optionalRef = (raw: unknown, field: string, resourceId: string): string | null =>
  raw === undefined || raw === null ? null : refKey(raw, field, resourceId)

const optionalNum = (raw: unknown, field: string): number | null => (raw === undefined || raw === null ? null : num(raw, field))

function kindOf(raw: unknown, field: string): CashReceiptKind {
  const value = text(raw, field)
  return (KINDS as readonly string[]).includes(value) ? (value as CashReceiptKind) : fail(`поле ${field} — неизвестный вид «${value}»`)
}

const UNIT_KINDS: readonly CashUnitKind[] = ['department', 'warehouse', 'other']

function unitKindOf(raw: unknown, field: string): CashUnitKind {
  const value = text(raw, field)
  return (UNIT_KINDS as readonly string[]).includes(value) ? (value as CashUnitKind) : 'other'
}

function receipt(raw: unknown, field: string, resourceId: string): CashReceipt {
  const item = record(raw, field)
  return {
    key: refKey(item.ref, `${field}.ref`, resourceId),
    kind: kindOf(item.kind, `${field}.kind`),
    number: optionalText(item.number, `${field}.number`),
    date: optionalText(item.date, `${field}.date`),
    posted: bool(item.posted, `${field}.posted`),
    registerKey: optionalRef(item.register, `${field}.register`, resourceId),
    registerName: optionalText(item.registerName, `${field}.registerName`),
    receiptNumber: optionalText(item.receiptNumber, `${field}.receiptNumber`),
    status: optionalText(item.status, `${field}.status`),
    amount: optionalNum(item.amount, `${field}.amount`),
    cardAmount: optionalNum(item.cardAmount, `${field}.cardAmount`),
    cashierKey: optionalRef(item.cashier, `${field}.cashier`, resourceId),
    cashierName: optionalText(item.cashierName, `${field}.cashierName`),
    shiftNumber: optionalText(item.shiftNumber, `${field}.shiftNumber`),
    // Старый ответ службы поля не знает: чек считается продажей.
    countsInSales: item.countsInSales === undefined ? true : bool(item.countsInSales, `${field}.countsInSales`),
  }
}

export function decodeCashRegisters(resourceId: string) {
  return (raw: unknown): CashRegisters => {
    const data = record(raw, 'data')
    return {
      registers: list(data.registers, 'registers', (entry, index) => {
        const item = record(entry, `registers[${index}]`)
        return {
          key: refKey(item.ref, `registers[${index}].ref`, resourceId),
          name: text(item.name, 'name'),
          code: optionalText(item.code, 'code'),
          organizationKey: optionalRef(item.organization, `registers[${index}].organization`, resourceId),
          unitKey: optionalRef(item.unit, `registers[${index}].unit`, resourceId),
        }
      }),
      organizations: list(data.organizations, 'organizations', (entry, index) => {
        const item = record(entry, `organizations[${index}]`)
        return { key: refKey(item.ref, `organizations[${index}].ref`, resourceId), name: text(item.name, 'name') }
      }),
      units: list(data.units, 'units', (entry, index) => {
        const item = record(entry, `units[${index}]`)
        return {
          key: refKey(item.ref, `units[${index}].ref`, resourceId),
          name: text(item.name, 'name'),
          kind: unitKindOf(item.kind, `units[${index}].kind`),
          organizationKey: optionalRef(item.organization, `units[${index}].organization`, resourceId),
          parentKey: optionalRef(item.parent, `units[${index}].parent`, resourceId),
        }
      }),
      observedAt: text(data.observedAt, 'observedAt'),
    }
  }
}

export function decodeCashReceipts(resourceId: string) {
  return (raw: unknown): CashReceipts => {
    const data = record(raw, 'data')
    return {
      items: list(data.items, 'items', (entry, index) => receipt(entry, `items[${index}]`, resourceId)),
      from: text(data.from, 'from'),
      to: text(data.to, 'to'),
      truncated: bool(data.truncated, 'truncated'),
      observedAt: text(data.observedAt, 'observedAt'),
    }
  }
}

export function decodeCashBounds() {
  return (raw: unknown): CashBounds => {
    const data = record(raw, 'data')
    return {
      first: optionalText(data.first, 'first'),
      last: optionalText(data.last, 'last'),
      observedAt: text(data.observedAt, 'observedAt'),
    }
  }
}

export function decodeCashSummary(resourceId: string) {
  return (raw: unknown): CashSummary => {
    const data = record(raw, 'data')
    return {
      from: text(data.from, 'from'),
      to: text(data.to, 'to'),
      rows: list(data.rows, 'rows', (entry, index) => {
        const item = record(entry, `rows[${index}]`)
        return {
          day: text(item.day, `rows[${index}].day`),
          registerKey: optionalRef(item.register, `rows[${index}].register`, resourceId),
          cashierKey: optionalRef(item.cashier, `rows[${index}].cashier`, resourceId),
          kind: kindOf(item.kind, `rows[${index}].kind`),
          count: num(item.count, `rows[${index}].count`),
          amount: num(item.amount, `rows[${index}].amount`),
          cardAmount: num(item.cardAmount, `rows[${index}].cardAmount`),
        }
      }),
      cashiers: list(data.cashiers, 'cashiers', (entry, index) => {
        const item = record(entry, `cashiers[${index}]`)
        return { key: refKey(item.ref, `cashiers[${index}].ref`, resourceId), name: text(item.name, `cashiers[${index}].name`) }
      }),
      truncated: bool(data.truncated, 'truncated'),
      observedAt: text(data.observedAt, 'observedAt'),
    }
  }
}

export function decodeCashReceipt(resourceId: string) {
  return (raw: unknown): CashReceiptDetails => {
    const data = record(raw, 'data')
    return {
      receipt: receipt(data.receipt, 'receipt', resourceId),
      lines: list(data.lines, 'lines', (entry, index): CashReceiptLine => {
        const item = record(entry, `lines[${index}]`)
        return {
          lineNumber: num(item.lineNumber, 'lineNumber'),
          productKey: optionalRef(item.product, `lines[${index}].product`, resourceId),
          productName: optionalText(item.productName, 'productName'),
          quantity: num(item.quantity, 'quantity'),
          price: optionalNum(item.price, 'price'),
          amount: optionalNum(item.amount, 'amount'),
          discount: optionalNum(item.discount, 'discount'),
        }
      }),
      payments: list(data.payments, 'payments', (entry, index) => num(record(entry, `payments[${index}]`).amount, 'amount')),
      observedAt: text(data.observedAt, 'observedAt'),
    }
  }
}
